import React from 'react'
import { useLocation, useNavigate } from 'react-router-dom';
import CategoryFilter from '../components/CategoryFilter.jsx';
import RecommendedSuppliers from '../components/RecommendedSuppliers.jsx';
import ProductCard from '../components/ProductCard.jsx';
import Pagination from '../components/Pagination.jsx';
import ConsumablesFilterBoxes from '../components/ConsumablesFilterBoxes.jsx';
import useAnalytics from '../hooks/useAnalytics';

export default function MarketplacePage({ onViewProduct, user, pageTitle, pageSubtitle, allowedCategories, basket, onAddToBasket, onUpdateBasketQuantity, onRemoveFromBasket, onShowLogin, onSwitchRole, onLogout, savedItems, onToggleSave }) {
    const navigate = useNavigate();
    const { trackEvent, consent } = useAnalytics();
    // This state was likely in a child component (ProductListPage), but is needed here
    // to pass the selected category to the new RecommendedSuppliers component.
    const [products, setProducts] = React.useState([]);
    const [categories, setCategories] = React.useState([]);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState(null);

    // Initialize selection from the URL query parameter (e.g., ?category=plant)
    const [selectedCategory, setSelectedCategory] = React.useState(() => {
        const params = new URLSearchParams(location.search);
        return params.get('category') || 'all';
    });

    // Synchronize the internal state with the URL when it changes
    React.useEffect(() => {
        const params = new URLSearchParams(location.search);
        const catFromUrl = params.get('category') || 'all';
        if (catFromUrl !== selectedCategory) {
            setSelectedCategory(catFromUrl);
            setCurrentPage(1);
        }
    }, [location.search]);

    // --- NEW: Facet State ---
    const [facets, setFacets] = React.useState({
        brand: '',
        minPrice: '',
        maxPrice: ''
    });

    // Store the counts returned by the API
    const [availableFacets, setAvailableFacets] = React.useState({
        brands: {},
        categories: {}
    });

    const [sortOption, setSortOption] = React.useState('date_desc');
    const [searchTerm, setSearchTerm] = React.useState('');
    const [currentPage, setCurrentPage] = React.useState(1);
    const [totalPages, setTotalPages] = React.useState(1);
    const [totalItems, setTotalItems] = React.useState(0);

    const [stickyHeight, setStickyHeight] = React.useState(0); // NEW: State for dynamic sticky height
    const fetchProducts = React.useCallback(() => {
        setLoading(true);
        const url = new URL("/api/products.php", window.location.origin);
        url.searchParams.append('category', selectedCategory);
        url.searchParams.append('sort', sortOption);
        url.searchParams.append('search', searchTerm);
        // Append Facets
        if (facets.brand) url.searchParams.append('brand', facets.brand);
        if (facets.maxPrice) url.searchParams.append('max_price', facets.maxPrice);
        
        url.searchParams.append('page', currentPage);
        url.searchParams.append('limit', 50);
        if (allowedCategories && allowedCategories.length > 0) {
            // Check if this is the miscellaneous page (contains bearings, filters, etc.)
            const isMiscPage = allowedCategories.includes('bearings') || 
                              allowedCategories.includes('filters') || 
                              allowedCategories.includes('conveyor-belts') ||
                              allowedCategories.includes('greases') ||
                              allowedCategories.includes('kg-pps') ||
                              allowedCategories.includes('diesel');
              
            if (isMiscPage) {
                // Send 'miscellaneous' to API to get all miscellaneous products
                url.searchParams.append('main_categories', 'miscellaneous');
            } else {
                url.searchParams.append('main_categories', allowedCategories.join(','));
            }

        }

        console.log('[MarketplacePage] API URL:', url.toString());
        console.log('[MarketplacePage] Parameters:', { category: selectedCategory, sort: sortOption, search: searchTerm, page: currentPage, limit: 50, main_categories: allowedCategories, facets });

        fetch(url, { credentials: 'include' })
            .then(res => {
                if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
                return res.json();
            })
            .then(data => {
                console.log('[MarketplacePage] API Response:', data);
                console.log('[MarketplacePage] Products count:', data.products?.length);
                console.log('[MarketplacePage] Sample products:', data.products?.slice(0, 5));
                setProducts(data.products);
                setTotalPages(data.pagination.totalPages);
                setTotalItems(data.pagination.totalItems);
                if (data.facets) setAvailableFacets(data.facets);
            })
            .catch(err => setError(err.message))
            .finally(() => setLoading(false));
    }, [selectedCategory, sortOption, searchTerm, currentPage, allowedCategories, facets]);

    const fetchCategories = React.useCallback(() => {
        fetch(`/api/categories.php?t=${new Date().getTime()}`, { credentials: 'include' })
            .then(res => res.json())
            .then(data => setCategories(data))
            .catch(err => setError(err.message));
    }, []);

    React.useEffect(() => {
        fetchCategories();
    }, [fetchCategories]);

    React.useEffect(() => {
        fetchProducts();
    }, [fetchProducts]);

    // Track pagination changes (skip initial mount)
    const prevPageRef = React.useRef(currentPage);
    React.useEffect(() => {
        if (prevPageRef.current !== currentPage && consent === 'granted') {
            trackEvent('pagination_changed', {
                eventData: { fromPage: prevPageRef.current, toPage: currentPage },
            });
        }
        prevPageRef.current = currentPage;
    }, [currentPage, consent, trackEvent]);

    const handleCategoryChange = (categorySlug) => {
        setSelectedCategory(categorySlug);
        setCurrentPage(1);
        if (consent === 'granted') {
            const cat = categories.find(c => c.slug === categorySlug);
            trackEvent('category_visited', {
                categoryId: cat?.id ?? null,
                categoryName: cat?.name || categorySlug,
                eventData: { categorySlug },
            });
        }
    };

    const handleSearchChange = (term) => {
        setSearchTerm(term);
        setCurrentPage(1);
        if (consent === 'granted' && term.trim()) {
            trackEvent('search_performed', {
                eventData: { searchTerm: term.trim() },
            });
        }
    };

    const handleSortChange = (sort) => {
        setSortOption(sort);
        setCurrentPage(1);
    };

    const handleSupplierClick = (supplierName, url) => {
        try {
            const formData = new FormData();
            formData.append('supplier_name', supplierName);
            navigator.sendBeacon('/api/track_supplier_click.php', formData);
        } catch (e) {
            console.error("Could not track supplier click:", e);
        }

        // Validate URL exists and is not empty
        if (!url || url.trim() === '') {
            console.error('Supplier link is missing or empty');
            return;
        }

        // Ensure URL has a protocol for external links
        let finalUrl = url;
        if (!url.startsWith('http://') && !url.startsWith('https://')) {
            // If it starts with /, treat as internal route
            if (url.startsWith('/')) {
                const path = url.replace(window.location.origin, '').replace('/miners-market-main', '');
                navigate(path);
                return;
            }
            // Otherwise, assume it's a domain and add https://
            finalUrl = 'https://' + url;
        }

        // Check if it's an internal link
        if (finalUrl.startsWith(window.location.origin) || finalUrl.startsWith('/')) {
            const path = finalUrl.replace(window.location.origin, '').replace('/miners-market-main', '');
            navigate(path);
        } else {
            // External link - use iOS/WKWebView-safe opener
            openExternalLink(finalUrl);
        }
    };

    // --- NEW: Detect Miscellaneous Landing State ---
    const isMiscLanding = allowedCategories && allowedCategories.includes('miscellaneous') && selectedCategory === 'all' && !searchTerm;

    const handleFacetChange = (key, value) => {
        setFacets(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1);
        if (consent === 'granted') {
            trackEvent('filters_applied', {
                eventData: { filterKey: key, filterValue: value },
            });
        }
    };

    const handleClearAll = () => {
        setFacets({brand:'', minPrice:'', maxPrice:''});
        setSearchTerm('');
        setSelectedCategory('all');
        setCurrentPage(1);
    };

    return (
        <div data-name="marketplace-page" data-file="pages/MarketplacePage.js" className="min-h-screen">
            <CategoryFilter
                categories={categories}
                selectedCategory={selectedCategory}
                setSelectedCategory={handleCategoryChange}
                searchTerm={searchTerm}
                setSearchTerm={handleSearchChange}
                pageTitle={pageTitle}
                pageSubtitle={pageSubtitle}
                allowedCategories={allowedCategories}
                availableFacets={availableFacets}
                facets={facets}
                onFacetChange={handleFacetChange}
                onClearAll={handleClearAll}
                user={user}
                onShowLogin={onShowLogin}
                onLogout={onLogout}
                basketCount={basket ? basket.reduce((acc, item) => acc + item.quantity, 0) : 0}
                savedItems={savedItems}
                sortOption={sortOption}
                setSortOption={handleSortChange}
                totalItems={totalItems}
                onStickyHeightChange={setStickyHeight} // NEW: Pass callback to update sticky height
                onSwitchRole={onSwitchRole} // Add role switching functionality
            />

            <div className="container mx-auto px-4 pb-4" style={{ paddingTop: '30px' }}> {/* NEW: Dynamic padding-top */}
                <RecommendedSuppliers
                    selectedCategory={selectedCategory}
                    allCategories={categories}
                    onSupplierClick={handleSupplierClick}
                />

                {/* --- NEW: Consumables Filter Boxes --- */}
                {allowedCategories && allowedCategories.includes('consumables') && (
                    <ConsumablesFilterBoxes
                        selectedCategory={selectedCategory}
                        onCategorySelect={handleCategoryChange}
                    />
                )}

                {/* --- NEW: Hide Product Grid on Misc Landing --- */}
                {true && (
                <div className="animate-fade-in product-grid-container-mobile">
                    {loading && <div className="text-center py-10"><i className="fas fa-spinner fa-spin text-4xl text-blue-500"></i></div>}
                    {error && <div className="text-center py-10 text-red-500">Error: {error}</div>}
                    {!loading && !error && products.length === 0 && <div className="text-center py-10 text-gray-500"><p className="text-lg">No products found.</p><p>Try adjusting your search or category filters.</p></div>}
                    {!loading && products.length > 0 && (
                        <div className="product-grid-mobile grid grid-cols-2 md:border-0 md:grid-cols-3 xl:grid-cols-4 md:divide-x-0 md:divide-y-0 md:gap-6">
                            {products.map(product => (
                                <ProductCard 
                                    key={`${product.listingType}-${product.id}`} 
                                    product={product} 
                                    badgeType="standard" 
                                    onViewDetails={() => onViewProduct({ ...product, badgeType: 'standard' })} 
                                    user={user}
                                    basket={basket}
                                    onAddToBasket={onAddToBasket}
                                    onUpdateBasketQuantity={onUpdateBasketQuantity}
                                    onRemoveFromBasket={onRemoveFromBasket}
                                    onShowLogin={onShowLogin}
                                    savedItems={savedItems}
                                    onToggleSave={onToggleSave}
                                />
                            ))}
                        </div>
                    )}
                    {totalPages > 1 && <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />}
                </div>
                )}
            </div>
        </div>
    );
}
